import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import ExitToAppOutlinedIcon from '@mui/icons-material/ExitToAppOutlined';
import { Box, Button, Divider, LinearProgress, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface DownloadOvaProps {
  type: 'endpoint' | 'service';
  node: string;
  onClose?: () => void | undefined;
  secondaryBtn?: () => void | undefined;
}
const DownloadOva = ({ node, type = 'endpoint', onClose, secondaryBtn }: DownloadOvaProps) => {
  const api = useApi();
  const endpointDownload = api.prepareEpnOvaApiV1PrepareEpnOvaGet.bind(null, {
    ep_node_id: node,
  });
  const serviceDownload = api.prepareSnOvaApiV1PrepareSnOvaGet.bind(null, {
    service_node_id: node,
  });
  const { data, refetch, isFetching, isSuccess } = useQuery(
    ['prepareNodeDownload'], type === 'endpoint' ? endpointDownload : serviceDownload,
    { enabled: false, cacheTime: 0, initialData: undefined },
  );
  const handleDownloadOva = () => {
    refetch();
  };

  const endpointAddress = useMutation(api.setEndpointNodeFormFactorApiV1SetEndpointNodeFormFactorPost, {
    onSuccess() {
      toast.success('Endpoint Node address received.');
      onClose?.();
    },
    onError: apiError,
  });
  const serviceAddress = useMutation(api.setServiceNodeFormFactorApiV1SetServiceNodeFormFactorPost, {
    onSuccess() {
      toast.success('Service Node address received.');
      onClose?.();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    if (type === 'endpoint') {
      endpointAddress.mutate({
        node_id: node,
        form_factor: 'ova',
      });
    } else {
      serviceAddress.mutate({
        node_id: node,
        form_factor: 'ova',
      });
    }
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 5 }}>
        To initiate the OVA download, click the prepare button. Once the progress bar ceases and the
        OVA is prepared, you may proceed to download it using the download button.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
        <Button
          variant="outlined"
          type="submit"
          disabled={isFetching || isSuccess}
          onClick={handleDownloadOva}
        >
          Prepare OVA for Download
        </Button>
        <Button
          variant="text"
          color="primary"
          disabled={!(data?.data as any)?.node_url}
          href={(data?.data as any)?.node_url}
          onClick={() => {
            onClose?.();
          }}
          endIcon={<ExitToAppOutlinedIcon fontSize="small" />}
        >
          Download OVA
        </Button>
      </Box>
      {isFetching && <LinearProgress variant="indeterminate" sx={{ width: '100%', mt: 5 }} />}
      <Divider sx={{ my: 5, width: '100%' }} />
      <Box display="flex" justifyContent={secondaryBtn ? 'space-between' : 'center'}>
        {secondaryBtn && (
          <Button
            variant="outlined"
            onClick={secondaryBtn}
          >
            Back
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          Save Form Factor
        </Button>
      </Box>
    </Box>
  );
};

export default DownloadOva;